import React, { useState, useEffect, useRef } from "react";
import {
  View,
  TouchableOpacity,
  I18nManager,
  Animated,
  Easing,
  StyleProp,
  ViewStyle,
  ViewProps,
} from "react-native";
import Collapsible from "react-native-collapsible";
import type { CollapsibleProps } from "react-native-collapsible";
import ArrowDownIcon, { ArrowDownIconStyling } from "./ArrowDownIcon";
import { MakePropsOptional } from "./types";
import { VDBText } from "@components/atoms";
import { useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import styled from "styled-components/native";
import { DefaultTheme } from "styled-components/native";

interface CollapsibleDetailViewProps {
  children: React.ReactNode;
  title?: string | React.ReactNode;
  initExpanded?: boolean;
  expanded?: boolean | null;
  unmountOnCollapse?: boolean;
  isRTL?: boolean | "auto";
  duration?: number;
  collapsibleProps?: MakePropsOptional<CollapsibleProps>;
  collapsibleContainerStyle?: ViewStyle;
  arrowStyling?: ArrowDownIconStyling;
  noArrow?: boolean;
  style?: StyleProp<ViewStyle>;
  activeOpacityFeedback?: number; // 0-1
  TouchableComponent?: React.ComponentType<any>;
  titleProps?: ViewProps;
  titleStyle?: ViewStyle;
  touchableWrapperProps?: ViewProps;
  setExpandView?: any;
  expandView?: boolean;
  forceExpand?: boolean;
  hideArrowBar?: boolean;
}

const CollapsibleDetailView = ({
  children,
  title = "",
  initExpanded = false,
  expanded = null,
  unmountOnCollapse = false,
  isRTL = "auto",
  duration = 300,
  collapsibleProps = {},
  collapsibleContainerStyle = {},
  arrowStyling,
  noArrow = false,
  activeOpacityFeedback = 0.3,
  TouchableComponent = TouchableOpacity,
  titleProps = {},
  titleStyle = {},
  touchableWrapperProps = {},
  setExpandView,
  forceExpand = false,
  hideArrowBar = false,
}: CollapsibleDetailViewProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();

  let controlled = expanded !== null;
  const [show, setShow] = useState<boolean | null>(initExpanded);
  const [mounted, setMounted] = useState(initExpanded);

  const rotateAnim = useRef(new Animated.Value(0)).current;

  if (controlled) {
    if (!mounted && expanded) setMounted(true);
  }

  const handleArrowRotate = (open: boolean | null = null) => {
    const _open = open === null ? show : open;
    if (!_open)
      Animated.timing(rotateAnim, {
        toValue: -180,
        duration,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
    else {
      Animated.timing(rotateAnim, {
        toValue: rotateAngle,
        duration,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
    }
  };

  const handleAnimationEnd = () => {
    if (unmountOnCollapse && !show) setMounted(false);
  };

  const handleToggleShow = () => {
    if (!controlled)
      if (forceExpand) {
        setShow(!show);
        setExpandView(!show);
        forceExpand = false;
      }
    if (!mounted) {
      if (!show) setMounted(true);
      setShow(!show);
      setExpandView(!show);
    } else {
      setShow(!show);
      setExpandView(!show);
    }
  };

  // place the arrow on the left or the right based on the device direction and isRTL property
  let rowDir: "row-reverse" | "row" = "row" as const;
  if (isRTL === "auto") isRTL = I18nManager.isRTL;
  else if (isRTL !== I18nManager.isRTL) rowDir = "row-reverse";

  const rotateAngle = isRTL ? 90 : 0;
  const rotateAnimDeg = rotateAnim.interpolate({
    inputRange: [0, 360],
    outputRange: ["0deg", "360deg"],
  });

  const TitleElement =
    typeof title === "string" ? (
      <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
        {title}
      </VDBText>
    ) : (
      title
    );

  useEffect(() => {
    if (mounted) {
      setShow(true);
    }
  }, [mounted]);

  useEffect(() => {
    rotateAnim.setValue(show ? 180 : rotateAngle);
  }, []);

  useEffect(() => {
    if (mounted) handleArrowRotate(!show);
    if (controlled && show !== expanded) setShow(expanded);
  });

  return (
    <Container>
      <View>
        <View
          style={{
            flexDirection: rowDir,
            alignItems: "center",
            padding: 10,
            ...titleStyle,
          }}
          {...titleProps}
        >
          <CardMain>{TitleElement}</CardMain>
        </View>
      </View>
      {mounted ? (
        <View style={{ width: "100%", ...collapsibleContainerStyle }}>
          <Collapsible
            onAnimationEnd={handleAnimationEnd}
            collapsed={!show}
            {...{ duration, ...collapsibleProps }}
          >
            {children}
          </Collapsible>
        </View>
      ) : null}
      {hideArrowBar ? null : (
        <BottomContainer>
          <TouchableComponent
            onPress={handleToggleShow}
            activeOpacity={activeOpacityFeedback}
            {...touchableWrapperProps}
          >
            <View
              style={{
                flexDirection: rowDir,
                alignItems: "center",
                justifyContent: "center",
                //padding: 10,
                ...titleStyle,
              }}
              {...titleProps}
            >
              {noArrow ? null : (
                <Animated.View
                  style={{ transform: [{ rotate: rotateAnimDeg }] }}
                >
                  <ArrowDownIcon {...arrowStyling} />
                </Animated.View>
              )}
            </View>
          </TouchableComponent>
        </BottomContainer>
      )}
    </Container>
  );
};

const CardMain = styled.View`
  flex: 1;
`;

const Container = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-01"] as string};
  border-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-04"] as string};
  align-content: flex-start;
  border-radius: 8px;
  padding-top: 5px;
  border-width: 1px;
`;

const BottomContainer = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-02"] as string};
  align-content: flex-start;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  padding-top: 5px;
  padding-bottom: 5px;
`;

export default CollapsibleDetailView;
